/**
 * Generic Supabase Realtime postgres_changes subscriber.
 *
 * Replaces the per-feature wrappers (hatif repository, the notifications
 * hook's inline `supabase.channel(...)` call) that all repeated the same
 * shape: open one channel, bind one postgres_changes listener, return an
 * unsubscribe callback. Every new "send an event to the frontend" use
 * case can now be one call to this helper.
 *
 * Why a single helper instead of letting each consumer roll their own:
 *   - The filter string syntax is footgun-y ("company_id=eq.<uuid>" with
 *     literal `eq.`). Centralising means we can keep one set of tests
 *     covering the contract.
 *   - Channels are scarce — each open WS-bound channel costs RAM and a
 *     server-side process. One factory makes it easier to audit channel
 *     names for collisions across the app.
 *   - The Supabase JS types for postgres_changes payloads are non-trivial;
 *     callers get a typed row payload here without having to import the
 *     `RealtimePostgres*Payload` types themselves.
 */
import type {
  RealtimePostgresDeletePayload,
  RealtimePostgresInsertPayload,
  RealtimePostgresUpdatePayload,
} from '@supabase/supabase-js';

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

// Postgres rows arrive over the WAL as JSON-shaped objects, so the
// constraint Supabase's `.on<T>` enforces internally (`{ [key: string]:
// any }`) is precisely the JSON-row shape spelled honestly. Domain types
// that want to be subscribed to via this helper declare a matching
// `[key: string]: Json | undefined` index signature so they explicitly
// claim "I am a JSON row," which is what they are at runtime anyway.
export type DatabaseRow = Record<string, Json | undefined>;

export interface SubscribeTableRowsParams<T extends DatabaseRow> {
  /**
   * Stable channel name. Embed identifying parts (e.g. companyId, userId)
   * so concurrent subscriptions across tabs/pages don't collide on the
   * same WS channel. Format convention: `"<table>:<scope>:<id>"`.
   */
  channelName: string;
  /** PostgreSQL schema. Defaults to "public". */
  schema?: string;
  /** Target table. postgres_changes binds to one table per listener. */
  table: string;
  /**
   * Postgres-changes filter string, e.g. `"company_id=eq.<uuid>"`. Defaults
   * to none — the channel will then receive every row event for the table.
   * For tenant-scoped subscriptions pass an explicit filter so cross-tenant
   * rows never even reach the client (RLS provides the security floor;
   * the filter is for cost + correctness against RLS-bypassing
   * misconfigurations).
   */
  filter?: string;
  /** Receives the inserted row. Omit to skip INSERT subscription entirely. */
  onInsert?: (row: T) => void;
  /**
   * Receives the new (full) row + the old row. Supabase only ships the
   * full prior row when the table is set to `REPLICA IDENTITY FULL` —
   * otherwise just the changed columns arrive — so `oldRow` is typed as
   * `Partial<T>`. Use `null` to signal "no prior data at all" (the
   * default-identity case where the WAL ships `{}`).
   */
  onUpdate?: (newRow: T, oldRow: Partial<T> | null) => void;
  /**
   * Receives the deleted row, typed as `Partial<T>` for the same
   * REPLICA IDENTITY reason — by default only the PK columns are
   * present. Consumers narrow when they need a specific field.
   */
  onDelete?: (oldRow: Partial<T>) => void;
}

/**
 * Open a Realtime channel and bind the requested postgres_changes listeners.
 * Returns the unsubscribe callback — call it from the consumer's effect
 * cleanup. If no handler is provided for an event type, that subscription
 * is skipped (one server-side binding saved per skipped event).
 */
export function subscribeTableRows<T extends DatabaseRow>(
  params: SubscribeTableRowsParams<T>
): () => void {
  const {
    channelName,
    schema = 'public',
    table,
    filter,
    onInsert,
    onUpdate,
    onDelete,
  } = params;

  let channel = supabase.channel(channelName);
  const base: { schema: string; table: string; filter?: string } = filter
    ? { schema, table, filter }
    : { schema, table };

  if (onInsert) {
    channel = channel.on<T>(
      'postgres_changes',
      { event: 'INSERT', ...base },
      (payload: RealtimePostgresInsertPayload<T>) => {
        if (payload.new) onInsert(payload.new);
      }
    );
  }
  if (onUpdate) {
    channel = channel.on<T>(
      'postgres_changes',
      { event: 'UPDATE', ...base },
      (payload: RealtimePostgresUpdatePayload<T>) => {
        if (payload.new) {
          // `old` is `{}` when REPLICA IDENTITY is default; signal that
          // with `null` so consumers don't iterate empty objects.
          const hasOld =
            payload.old !== undefined && Object.keys(payload.old).length > 0;
          onUpdate(payload.new, hasOld ? payload.old : null);
        }
      }
    );
  }
  if (onDelete) {
    channel = channel.on<T>(
      'postgres_changes',
      { event: 'DELETE', ...base },
      (payload: RealtimePostgresDeletePayload<T>) => {
        if (payload.old) onDelete(payload.old);
      }
    );
  }

  channel.subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
}
